/**
 * Flyable aircraft continuity on the canonical board.
 *
 * Every open leg's origin must match where that tail ends the previous
 * non-cancelled sector (or a ferry is inserted first). Grok / Classic /
 * Megan / desk retimes go through `reconcileFlyableBoard` so CYPD→CYYZ
 * cannot be followed by another CYPD outbound without CYYZ→CYPD.
 */
import { MIN_TURN_MIN, repositionBetween } from "./assignment";
import { stampCancel, stampReinstate } from "./cancelReason";
import { isOpenBoardMission, missionStaMs, missionStdMs, type DispatchBrainInput } from "./dispatchBrain";
import { planCharterFerry } from "./ferryInsert";
import { resolveBoardOverlaps } from "./scheduleShift";
import { confirmRequestFor, newSlipBatchId, recordSlip } from "./slipJournal";
import type { AircraftSpec, Mission, SceneryAirport } from "./types";
import type { TripAssignment } from "./assignment";

const MAX_REPAIR_PASSES = 8;

export type ContinuityBreak = {
  tailId: string;
  registration: string;
  earlierId: string;
  laterId: string;
  fromIcao: string;
  needOrigin: string;
  message: string;
};

export type FlyableBoardResult = {
  missions: Mission[];
  assignments: TripAssignment[];
  insertedFerryIds: string[];
  cancelledIds: string[];
  shifted: boolean;
  breaks: ContinuityBreak[];
};

function icao(value?: string | null): string {
  return (value ?? "").trim().toUpperCase();
}

function openForTail(missions: Mission[], aircraftId: string): Mission[] {
  return missions
    .filter((m) => m.aircraftId === aircraftId && isOpenBoardMission(m.status))
    .sort((a, b) => missionStdMs(a) - missionStdMs(b) || a.id.localeCompare(b.id));
}

function alreadyCoversHop(missions: Mission[], fromIcao: string, toIcao: string, aircraftId: string, beforeStdMs: number): Mission | undefined {
  return missions.find((m) => {
    if (!isOpenBoardMission(m.status) || m.flightType !== "ferry") return false;
    if (m.aircraftId !== aircraftId) return false;
    if (icao(m.originIcao) !== icao(fromIcao) || icao(m.destIcao) !== icao(toIcao)) return false;
    return missionStdMs(m) <= beforeStdMs + 60_000;
  });
}

/**
 * A ferry this repair already built for the same tail and hop, cancelled since by
 * a sweep or a hand cancel. Without this the repair mints a brand new ticket on
 * every board write — the board and the Flight Log grow forever and the ferry
 * numbers mash (CEL1007F, CEL1007F2 … CEL1007F7).
 */
function cancelledFerryForHop(
  missions: Mission[],
  fromIcao: string,
  toIcao: string,
  later: Mission,
): Mission | undefined {
  return missions.find((m) => {
    if (m.status !== "cancelled" || m.flightType !== "ferry" || m.isMaintenance) return false;
    if (m.aircraftId !== later.aircraftId) return false;
    if (icao(m.originIcao) !== icao(fromIcao) || icao(m.destIcao) !== icao(toIcao)) return false;
    if (m.parentMissionId && m.parentMissionId === later.id) return true;
    return Boolean(later.inboxRequestId) && m.inboxRequestId === later.inboxRequestId;
  });
}

function slipMission(mission: Mission, departureIso: string, note: string, now: Date): Mission | null {
  if (confirmRequestFor(mission, departureIso)) return null;
  const slipped = recordSlip(mission, departureIso, {
    cause: "auto_resolve",
    at: now.toISOString(),
    batchId: newSlipBatchId(now),
  });
  return {
    ...slipped,
    dispatchWarning: [mission.dispatchWarning, note].filter(Boolean).join(" · "),
  };
}

function patchById(missions: Mission[], next: Mission): Mission[] {
  return missions.map((m) => (m.id === next.id ? next : m));
}

export function detectContinuityBreaks(
  missions: readonly Mission[],
  fleet: readonly AircraftSpec[],
): ContinuityBreak[] {
  const breaks: ContinuityBreak[] = [];
  const tailIds = new Set(missions.map((m) => m.aircraftId).filter(Boolean));
  for (const tailId of tailIds) {
    const spec = fleet.find((a) => a.id === tailId);
    const list = openForTail([...missions], tailId);
    for (let i = 0; i < list.length - 1; i++) {
      const earlier = list[i]!;
      const later = list[i + 1]!;
      if (icao(earlier.destIcao) === icao(later.originIcao)) continue;
      breaks.push({
        tailId,
        registration: spec?.registration || earlier.registration,
        earlierId: earlier.id,
        laterId: later.id,
        fromIcao: earlier.destIcao,
        needOrigin: later.originIcao,
        message: `${spec?.registration || earlier.registration} ends ${earlier.originIcao}–${earlier.destIcao} at ${earlier.destIcao} but ${later.originIcao}–${later.destIcao} still departs ${later.originIcao} — missing ferry.`,
      });
    }
  }
  return breaks;
}

function insertOrSlipFerry(input: {
  missions: Mission[];
  earlier: Mission;
  later: Mission;
  spec: AircraftSpec | undefined;
  scenery: SceneryAirport[];
  now: Date;
}): { missions: Mission[]; inserted?: Mission; slipped?: boolean; cancelLater?: boolean } {
  const fromIcao = icao(input.earlier.destIcao);
  const toIcao = icao(input.later.originIcao);
  if (!fromIcao || !toIcao || fromIcao === toIcao) return { missions: input.missions };
  const existing = alreadyCoversHop(input.missions, fromIcao, toIcao, input.later.aircraftId, missionStdMs(input.later));
  if (existing) {
    const ferrySta = missionStaMs(existing);
    const need = ferrySta + MIN_TURN_MIN * 60_000;
    if (missionStdMs(input.later) + 1000 >= need) return { missions: input.missions };
    const iso = new Date(need).toISOString();
    const slipped = slipMission(input.later, iso, `SCHED  slipped STD to ${iso} after inbound ferry ${existing.callsign}.`, input.now);
    if (!slipped) return { missions: input.missions };
    return { missions: patchById(input.missions, slipped), slipped: true };
  }
  if (!input.spec) return { missions: input.missions, cancelLater: true };
  const planned = planCharterFerry({
    spec: input.spec,
    fromIcao,
    toIcao,
    charterStd: input.later.departureIso,
    scenery: input.scenery,
    now: input.now,
    linkRequestId: input.later.inboxRequestId,
    parentMissionId: input.later.id,
    needed: true,
    existing: input.missions,
  });
  if (!planned.ferry) {
    const repo = repositionBetween(fromIcao, toIcao, input.spec, input.scenery);
    const earliest = missionStaMs(input.earlier) + (repo.eteMin + MIN_TURN_MIN) * 60_000;
    if (earliest - missionStdMs(input.later) <= 18 * 60 * 60_000) {
      const iso = new Date(Math.max(earliest, input.now.getTime())).toISOString();
      const slipped = slipMission(
        input.later,
        iso,
        `SCHED  slipped STD to ${iso} so ${fromIcao}–${toIcao} can reposition.`,
        input.now,
      );
      if (!slipped) return { missions: input.missions };
      return { missions: patchById(input.missions, slipped), slipped: true };
    }
    return { missions: input.missions, cancelLater: true };
  }
  let ferry = planned.ferry;
  const ferryEarliest = missionStaMs(input.earlier) + MIN_TURN_MIN * 60_000;
  if (missionStdMs(ferry) < ferryEarliest) {
    const moved = slipMission(
      ferry,
      new Date(ferryEarliest).toISOString(),
      `SCHED  ferry after ${input.earlier.callsign} + turn.`,
      input.now,
    );
    if (moved) ferry = moved;
  }
  // Reinstate the ferry ticket this repair already owns instead of minting another.
  const priorFerry = cancelledFerryForHop(input.missions, fromIcao, toIcao, input.later);
  if (priorFerry) {
    ferry = {
      ...stampReinstate(priorFerry, { actor: "desk", at: input.now.toISOString() }),
      departureIso: ferry.departureIso,
      eteMin: ferry.eteMin,
      legs: ferry.legs,
      dispatchWarning: `SCHED  reinstated auto ferry ${fromIcao}–${toIcao} — one ferry ticket per hop.`,
    };
    let reused = patchById(input.missions, ferry);
    const arriveReused = missionStaMs(ferry) + MIN_TURN_MIN * 60_000;
    if (missionStdMs(input.later) + 1000 < arriveReused) {
      const iso = new Date(arriveReused).toISOString();
      const slipped = slipMission(input.later, iso, `SCHED  slipped STD to ${iso} after auto ferry ${fromIcao}–${toIcao}.`, input.now);
      if (!slipped) return { missions: reused, inserted: ferry };
      return { missions: patchById(reused, slipped), inserted: ferry, slipped: true };
    }
    return { missions: reused, inserted: ferry };
  }
  // A reused open ferry keeps its id; append only a ticket the board does not hold yet.
  let missions = input.missions.some((m) => m.id === ferry.id) ? patchById(input.missions, ferry) : [...input.missions, ferry];
  const arrive = missionStaMs(ferry) + MIN_TURN_MIN * 60_000;
  if (missionStdMs(input.later) + 1000 < arrive) {
    const iso = new Date(arrive).toISOString();
    const slipped = slipMission(input.later, iso, `SCHED  slipped STD to ${iso} after auto ferry ${fromIcao}–${toIcao}.`, input.now);
    if (!slipped) return { missions, inserted: ferry };
    return { missions: patchById(missions, slipped), inserted: ferry, slipped: true };
  }
  return { missions, inserted: ferry };
}

/**
 * Overlap slip + missing-ferry insert + scheduling cancel when the pair
 * cannot be made flyable within 18 hours. Pure — store writes + numbers ferries.
 */
export function reconcileFlyableBoard(input: DispatchBrainInput): FlyableBoardResult {
  const now = input.now ?? new Date();
  const overlap = resolveBoardOverlaps(input);
  let missions = overlap.missions;
  let shifted = overlap.shifted;
  const insertedFerryIds: string[] = [];
  const cancelledIds: string[] = [];

  for (let pass = 0; pass < MAX_REPAIR_PASSES; pass++) {
    const breaks = detectContinuityBreaks(missions, input.fleet);
    if (!breaks.length) break;
    let changed = false;
    for (const row of breaks) {
      const earlier = missions.find((m) => m.id === row.earlierId);
      const later = missions.find((m) => m.id === row.laterId);
      if (!earlier || !later || !isOpenBoardMission(later.status)) continue;
      const spec = input.fleet.find((a) => a.id === row.tailId);
      const out = insertOrSlipFerry({ missions, earlier, later, spec, scenery: input.scenery, now });
      missions = out.missions;
      if (out.inserted) {
        insertedFerryIds.push(out.inserted.id);
        changed = true;
      }
      if (out.slipped) {
        shifted = true;
        changed = true;
      }
      if (out.cancelLater) {
        const iso = now.toISOString();
        missions = missions.map((m) =>
          m.id === later.id
            ? stampCancel(m, {
                cancelReason: "scheduling",
                cancelReasonNote: row.message,
                at: iso,
              })
            : m,
        );
        cancelledIds.push(later.id);
        changed = true;
      }
    }
    if (!changed) break;
  }

  if (insertedFerryIds.length) {
    const postOverlap = resolveBoardOverlaps({ ...input, missions });
    if (postOverlap.shifted) {
      missions = postOverlap.missions;
      shifted = true;
    }
  }

  return {
    missions,
    assignments: overlap.assignments,
    insertedFerryIds,
    cancelledIds,
    shifted,
    breaks: detectContinuityBreaks(missions, input.fleet),
  };
}
